import { practicalLearnContent } from "./practicalContent.js";
import type { LearnPracticalContent } from "./practicalContent.js";
import { learnResearchMetadata } from "./researchMetadata.js";
import type { LearnResearchMetadata } from "./researchMetadata.js";

export type LearnCategory = "everyday-risks" | "disasters" | "survival";

export interface LearnContentRecord {
  readonly metadata: LearnResearchMetadata;
  readonly practical: LearnPracticalContent;
  readonly category: LearnCategory;
  readonly image: string;
  readonly relatedSlugs: readonly string[];
}

const categoryBySlug: Readonly<Record<string, LearnCategory>> = {
  earthquake: "disasters",
  tsunami: "disasters",
  "flood-storm": "disasters",
  "fire-evacuation": "disasters",
  chemicals: "everyday-risks",
  "first-aid": "everyday-risks",
  "public-safety": "survival",
  "family-preparedness": "survival",
  medicines: "everyday-risks",
  "food-labels": "everyday-risks",
};

const imageByCategory: Readonly<Record<LearnCategory, string>> = {
  "everyday-risks": "/images/learning-everyday-v1.webp",
  disasters: "/images/learning-weather-v1.webp",
  survival: "/images/learning-outdoors-v1.webp",
};

const relatedBySlug: Readonly<Record<string, readonly string[]>> = {
  earthquake: ["tsunami", "family-preparedness"],
  tsunami: ["earthquake", "flood-storm"],
  "flood-storm": ["family-preparedness", "fire-evacuation"],
  "fire-evacuation": ["family-preparedness", "first-aid"],
  chemicals: ["first-aid", "medicines"],
  "first-aid": ["chemicals", "family-preparedness"],
  "public-safety": ["family-preparedness", "first-aid"],
  "family-preparedness": ["flood-storm", "earthquake"],
  medicines: ["food-labels", "first-aid"],
  "food-labels": ["medicines", "first-aid"],
};

/** The only read path for the ten canonical, bundled Learn records. */
export const learnContentRecords: readonly LearnContentRecord[] = learnResearchMetadata.map(
  (metadata) => {
    const category = categoryBySlug[metadata.slug];
    const practical = practicalLearnContent[metadata.slug];
    if (!category || !practical)
      throw new Error(`Incomplete Learn content record: ${metadata.slug}`);
    return {
      metadata,
      practical,
      category,
      image: imageByCategory[category],
      relatedSlugs: relatedBySlug[metadata.slug] ?? [],
    };
  },
);

export const learnCategories = [
  {
    id: "everyday-risks",
    image: imageByCategory["everyday-risks"],
    th: {
      title: "ภัยใกล้ตัว",
      description: "เข้าใจอุบัติเหตุ สารเคมี ยา อาหาร และการช่วยเหลือเบื้องต้นในชีวิตประจำวัน",
    },
    en: {
      title: "Everyday risks",
      description: "Practical guidance for accidents, chemicals, medicines, food, and first aid.",
    },
  },
  {
    id: "disasters",
    image: imageByCategory.disasters,
    th: {
      title: "ภัยพิบัติ",
      description: "เตรียมรับแผ่นดินไหว สึนามิ น้ำท่วม พายุ และเหตุไฟไหม้อย่างเป็นขั้นตอน",
    },
    en: {
      title: "Disasters",
      description: "Prepare for earthquakes, tsunamis, floods, storms, and fire evacuation.",
    },
  },
  {
    id: "survival",
    image: imageByCategory.survival,
    th: {
      title: "เตรียมพร้อมและปลอดภัย",
      description: "วางแผนครอบครัวและรู้ทางเลือกที่ปลอดภัยเมื่ออยู่ในพื้นที่สาธารณะ",
    },
    en: {
      title: "Preparedness and safety",
      description: "Plan as a household and know safer options in public places.",
    },
  },
] as const;

export function findLearnContentRecord(slug: string | undefined): LearnContentRecord | undefined {
  return learnContentRecords.find((record) => record.metadata.slug === slug);
}
